import React, { useEffect, useMemo, useState } from 'react';
import {
  Mail,
  Plus,
  Pencil,
  Trash2,
  Send,
  Calendar,
  XCircle,
  X,
  Loader2,
  Search,
  Eye,
} from 'lucide-react';
import { ToastContainer, useToast } from '@/shared/components/ui/Toast';
import { CuteConfirmModal } from '@/shared/components/CuteConfirmModal';
import {
  getCampaigns,
  createCampaign,
  updateCampaign,
  deleteCampaign,
  scheduleCampaign,
  sendCampaign,
  cancelCampaign,
  getTemplates,
  EmailCampaign,
  EmailTemplate,
  CreateCampaignDto,
  getCampaignStatusColor,
  getCampaignStatusLabel,
  renderTemplate,
} from '@/services/emailCrmService';
import { RecipientSelector, RecipientSelection } from './components/RecipientSelector';

const SAMPLE_LEAD = {
  email: 'sample@nexedu.ma',
  firstName: 'Yasmine',
  lastName: 'Bennani',
  fullName: 'Yasmine Bennani',
  phone: '+212 6 12 34 56 78',
  company: 'Acme Education',
  jobTitle: 'Directrice générale',
  city: 'Casablanca',
  country: 'Maroc',
  customFields: { experience_years: 8 },
};

interface FormState extends CreateCampaignDto {
  recipients: RecipientSelection;
}

const emptyForm: FormState = {
  name: '',
  subject: '',
  content: '',
  templateId: undefined,
  targetType: 'ALL_LEADS',
  targetIds: [],
  recipients: { targetType: 'ALL_LEADS', targetIds: [] },
};

export const EmailCampaignsPage: React.FC = () => {
  const { toasts, removeToast, success, error } = useToast();

  const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);

  const [showSchedule, setShowSchedule] = useState<EmailCampaign | null>(null);
  const [scheduleAt, setScheduleAt] = useState('');

  const [showPreview, setShowPreview] = useState<EmailCampaign | null>(null);

  // Single confirm state used by all destructive / important actions
  const [confirmState, setConfirmState] = useState<{
    title: string;
    message: string;
    itemName?: string;
    confirmLabel: string;
    variant: 'danger' | 'warning' | 'info' | 'success';
    onConfirm: () => Promise<void> | void;
  } | null>(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [c, t] = await Promise.all([getCampaigns(), getTemplates()]);
      setCampaigns(c);
      setTemplates(t);
    } catch (err: any) {
      console.error(err);
      error('Erreur', err?.message ?? 'Impossible de charger les campagnes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    return campaigns.filter((c) => {
      if (statusFilter && c.status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          c.name.toLowerCase().includes(q) ||
          c.subject.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [campaigns, search, statusFilter]);

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowForm(true);
  };

  const openEdit = async (c: EmailCampaign) => {
    setEditingId(c.id);
    setFormData({
      name: c.name,
      subject: c.subject,
      content: c.content,
      templateId: c.templateId ?? undefined,
      targetType: c.targetType as any,
      targetIds: c.targetIds ?? [],
      recipients: {
        targetType: (c.targetType as any) ?? 'ALL_LEADS',
        targetIds: c.targetIds ?? [],
      },
    });
    setShowForm(true);
  };

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === formData.templateId) ?? null,
    [templates, formData.templateId],
  );

  const handleSubmit = async () => {
    if (!formData.name.trim()) return error('Nom requis', '');
    if (!formData.templateId) return error('Sélectionnez un template', '');
    setSaving(true);
    try {
      const payload: CreateCampaignDto = {
        name: formData.name,
        // Subject/content come from the template at send time, but the API still
        // wants something here — pass the template's values for visibility.
        subject: selectedTemplate?.subject ?? '',
        content: selectedTemplate?.content ?? '',
        templateId: formData.templateId,
        targetType: formData.recipients.targetType,
        targetIds: formData.recipients.targetIds,
      };
      if (editingId) {
        await updateCampaign(editingId, payload);
        success('Campagne mise à jour', '');
      } else {
        await createCampaign(payload);
        success('Brouillon créé', '');
      }
      setShowForm(false);
      fetchAll();
    } catch (err: any) {
      error('Erreur', err?.message ?? 'Échec');
    } finally {
      setSaving(false);
    }
  };

  const askDelete = (c: EmailCampaign) => {
    setConfirmState({
      title: 'Supprimer la campagne',
      message: 'Cette action est définitive — la campagne et son historique seront effacés.',
      itemName: c.name,
      confirmLabel: 'Supprimer',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await deleteCampaign(c.id);
          success('Supprimée', '');
          fetchAll();
        } catch (err: any) {
          error('Erreur', err?.message ?? 'Suppression impossible');
        }
      },
    });
  };

  const askSend = (c: EmailCampaign) => {
    setConfirmState({
      title: 'Envoyer maintenant',
      message:
        'Les utilisateurs déjà inscrits à la plateforme et les désinscrits seront automatiquement retirés.',
      itemName: c.name,
      confirmLabel: 'Envoyer',
      variant: 'success',
      onConfirm: async () => {
        try {
          await sendCampaign(c.id);
          success('Envoi déclenché', 'Les emails sont en cours de remise.');
          fetchAll();
        } catch (err: any) {
          error('Erreur', err?.message ?? "Impossible d'envoyer");
        }
      },
    });
  };

  const handleSchedule = async () => {
    if (!showSchedule || !scheduleAt) return;
    try {
      await scheduleCampaign(showSchedule.id, scheduleAt);
      success('Planifiée', 'La campagne sera envoyée automatiquement.');
      setShowSchedule(null);
      setScheduleAt('');
      fetchAll();
    } catch (err: any) {
      error('Erreur', err?.message ?? 'Planification impossible');
    }
  };

  const askCancel = (c: EmailCampaign) => {
    setConfirmState({
      title: 'Annuler la campagne',
      message:
        "La campagne ne sera pas envoyée. Vous pourrez la reprogrammer ou l'envoyer plus tard.",
      itemName: c.name,
      confirmLabel: 'Oui, annuler',
      variant: 'warning',
      onConfirm: async () => {
        try {
          await cancelCampaign(c.id);
          success('Annulée', '');
          fetchAll();
        } catch (err: any) {
          error('Erreur', err?.message ?? 'Annulation impossible');
        }
      },
    });
  };

  const openSchedule = (c: EmailCampaign) => {
    setShowSchedule(c);
    const d = new Date(Date.now() + 5 * 60 * 1000);
    setScheduleAt(d.toISOString().slice(0, 16));
  };

  const previewCampaign = useMemo(() => {
    if (!showPreview) return null;
    const tpl = templates.find((t) => t.id === showPreview.templateId);
    return {
      subject: renderTemplate(tpl?.subject ?? showPreview.subject, {
        lead: SAMPLE_LEAD,
        unsubscribeUrl: '#',
      }),
      body: renderTemplate(tpl?.content ?? showPreview.content, {
        lead: SAMPLE_LEAD,
        unsubscribeUrl: '#',
      }),
    };
  }, [showPreview, templates]);

  return (
    <>
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Campagnes d'email</h1>
            <p className="text-sm text-gray-500 mt-1">
              Envois marketing one-shot ou planifiés vers vos leads. Les utilisateurs déjà inscrits ne reçoivent jamais ces emails.
            </p>
          </div>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
          >
            <Plus size={16} />
            Nouvelle campagne
          </button>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher une campagne…"
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white"
          >
            <option value="">Tous les statuts</option>
            <option value="DRAFT">Brouillons</option>
            <option value="SCHEDULED">Planifiées</option>
            <option value="SENDING">En cours</option>
            <option value="SENT">Envoyées</option>
            <option value="CANCELLED">Annulées</option>
          </select>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="animate-spin text-indigo-600" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
            <Mail className="mx-auto mb-3 text-gray-300" size={48} />
            <p className="text-gray-500">Aucune campagne pour le moment.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Nom</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Statut</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Programmée</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase">Envoyés</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase">Délivrés</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-900">{c.name}</p>
                      <p className="text-xs text-gray-500 truncate max-w-md">{c.subject}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${getCampaignStatusColor(c.status)}`}
                      >
                        {getCampaignStatusLabel(c.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {c.scheduledAt ? new Date(c.scheduledAt).toLocaleString('fr-FR') : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700">
                      {(c as any).totalSent ?? 0}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700">
                      {(c as any).totalDelivered ?? 0}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <button
                          onClick={() => setShowPreview(c)}
                          className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"
                          title="Aperçu"
                        >
                          <Eye size={14} />
                        </button>
                        {c.status === 'DRAFT' && (
                          <>
                            <button
                              onClick={() => openEdit(c)}
                              className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded"
                              title="Modifier"
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              onClick={() => openSchedule(c)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                              title="Planifier"
                            >
                              <Calendar size={14} />
                            </button>
                            <button
                              onClick={() => askSend(c)}
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                              title="Envoyer maintenant"
                            >
                              <Send size={14} />
                            </button>
                            <button
                              onClick={() => askDelete(c)}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                              title="Supprimer"
                            >
                              <Trash2 size={14} />
                            </button>
                          </>
                        )}
                        {c.status === 'SCHEDULED' && (
                          <>
                            <button
                              onClick={() => openSchedule(c)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                              title="Reprogrammer"
                            >
                              <Calendar size={14} />
                            </button>
                            <button
                              onClick={() => askCancel(c)}
                              className="p-1.5 text-amber-600 hover:bg-amber-50 rounded"
                              title="Annuler"
                            >
                              <XCircle size={14} />
                            </button>
                          </>
                        )}
                        {c.status === 'CANCELLED' && (
                          <>
                            <button
                              onClick={() => openSchedule(c)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                              title="Reprogrammer"
                            >
                              <Calendar size={14} />
                            </button>
                            <button
                              onClick={() => askSend(c)}
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                              title="Envoyer maintenant"
                            >
                              <Send size={14} />
                            </button>
                            <button
                              onClick={() => askDelete(c)}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                              title="Supprimer"
                            >
                              <Trash2 size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingId ? 'Modifier la campagne' : 'Nouvelle campagne'}
              </h2>
              <button onClick={() => setShowForm(false)} className="p-1.5 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom de la campagne *
                </label>
                <input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Newsletter Avril 2026"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template *
                </label>
                <select
                  value={formData.templateId ?? ''}
                  onChange={(e) =>
                    setFormData({ ...formData, templateId: e.target.value || undefined })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">— Sélectionner un template —</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                {selectedTemplate ? (
                  <div className="mt-2 p-3 bg-gray-50 rounded-lg text-sm">
                    <p className="text-gray-700">
                      <strong>Objet :</strong> {selectedTemplate.subject}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Le contenu et les variables sont définis dans le template. Ils seront rendus pour chaque destinataire au moment de l'envoi.
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    Choisissez un template pour définir l'objet et le contenu de la campagne.
                  </p>
                )}
              </div>

              <RecipientSelector
                value={formData.recipients}
                onChange={(r) => setFormData((prev) => ({ ...prev, recipients: r }))}
              />
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-gray-200 sticky bottom-0 bg-white">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-2"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : null}
                {editingId ? 'Enregistrer' : 'Créer le brouillon'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule modal */}
      {showSchedule && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Planifier l'envoi</h2>
              <button onClick={() => setShowSchedule(null)} className="p-1.5 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-700 mb-3">
                « {showSchedule.name} »
              </p>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date et heure d'envoi
              </label>
              <input
                type="datetime-local"
                value={scheduleAt}
                onChange={(e) => setScheduleAt(e.target.value)}
                min={new Date().toISOString().slice(0, 16)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Le scheduler vérifie chaque minute les campagnes à envoyer.
              </p>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-gray-200">
              <button
                onClick={() => setShowSchedule(null)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleSchedule}
                disabled={!scheduleAt}
                className="px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                Planifier
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {showPreview && previewCampaign && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Aperçu : {showPreview.name}
              </h2>
              <button onClick={() => setShowPreview(null)} className="p-1.5 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>
            <div className="p-5 bg-gray-50">
              <p className="text-xs text-gray-500 mb-2">Aperçu avec un lead exemple</p>
              <div className="bg-white rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-2 pb-2 border-b border-gray-100">
                  <strong>Objet :</strong> {previewCampaign.subject}
                </p>
                <div
                  className="prose prose-sm max-w-none"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: previewCampaign.body }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmState && (
        <CuteConfirmModal
          isOpen
          title={confirmState.title}
          message={confirmState.message}
          itemName={confirmState.itemName}
          confirmLabel={confirmState.confirmLabel}
          variant={confirmState.variant}
          onConfirm={async () => {
            const action = confirmState.onConfirm;
            setConfirmState(null);
            await action();
          }}
          onCancel={() => setConfirmState(null)}
        />
      )}
    </>
  );
};

export default EmailCampaignsPage;
